// src/components/pos/ManagerGate.js
//
// The single manager-authorisation gate for restricted POS actions
// (discount, staff allowance, deleting an order, reordering the menu, and
// device configuration).
//
// The PIN pad is lifted from pos-app/StationSettingsSheet.js, which is now a
// consumer rather than a second implementation. Two properties matter and
// must survive any refactor:
//
//   1. It authenticates through POST /manager-approvals/verify, which mints
//      NO token. Routing this through authService.employeeLogin would write
//      token/user_role/employee_name/employee_id/auth_type to localStorage
//      and replace the device's active operator session — a manager
//      approving a cashier's discount would silently become the operator
//      subsequent sales are attributed to. That endpoint also writes the
//      audit row in the same transaction as the credential check, so an
//      approval cannot be forged by the cashier it is meant to police.
//   2. onPointerDown, not onClick — see OperatorLockScreen.js's header for
//      the mechanism. Consistent across every native PIN pad.
//
// This file lives in components/pos/ rather than pos-app/ because the legacy
// web/Electron till consumes it too, and neither surface should import
// across the other's boundary. The CSS uses the global theme tokens from
// styles/theme.css with literal fallbacks, so it renders correctly on the
// legacy Tailwind pages too.
// Portal rationale — this gate is `position:fixed` chrome, and per the CSS
// spec any ancestor with a non-`none` backdrop-filter / filter / transform
// becomes the CONTAINING BLOCK for fixed descendants. Every one of this
// component's six call sites sits inside such an ancestor:
//   StationSettingsSheet  .sts-panel      backdrop-filter:blur(22px)
//   CartPanel             .crt-root       backdrop-filter:blur(20px)
//   CatalogBrowse         (station glass chrome)
//   OrdersPanelNative     .opn-panel      backdrop-filter:blur(24px)
//   StaffAllowanceManager (hosted inside the cashier surface)
//   CashierSalesPage      (legacy till)
//
// Un-portaled, the gate sized itself to whichever panel opened it. Reported
// from the field as "the keypad is clipped to a narrow horizontal band —
// 1-2-3 and 4-5-6 show, 7-8-9 is cut off mid-glyph": the pad was rendering
// inside StationSettingsSheet's 400px panel, which is `max-height:100%` of
// itself, not of the screen.
//
// document.body is the one node with no filter/backdrop-filter/transform
// ancestor. Same fix already applied to components/common/Modal.js and,
// locally, to CartPanel / OrdersPanelNative / TopBarExtras / OrderDrawer.
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { managerApprovalService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const PIN_LENGTH = 6;

export const isManagerRole = (role) => {
    const normalized = (role || '').toLowerCase();
    return normalized === 'manager' || normalized === 'admin';
};

const CSS = `
@keyframes mgateShake{0%,100%{transform:translateX(0);}25%{transform:translateX(-7px);}75%{transform:translateX(7px);}}
.mgate-scrim{position:fixed;inset:0;z-index:2200;display:flex;align-items:center;justify-content:center;
  padding:20px;box-sizing:border-box;background:rgba(2,8,23,.55);
  -webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);}
.mgate-panel{width:330px;max-width:100%;box-sizing:border-box;border-radius:20px;padding:24px 22px 20px;
  background:var(--c-surface,#ffffff);border:1px solid var(--c-border,#e2e8f0);
  box-shadow:0 18px 48px rgba(2,8,23,.28);
  font-family:'Plus Jakarta Sans',sans-serif;}
.mgate-title{margin:0;font-size:17px;font-weight:800;letter-spacing:-.2px;text-align:center;
  color:var(--c-ink,#212529);}
.mgate-sub{margin:6px 0 0;font-size:12.5px;font-weight:600;line-height:1.45;text-align:center;
  color:var(--c-muted,#64748b);}
.mgate-err{margin:12px 0 0;padding:9px 12px;border-radius:11px;text-align:center;
  font-size:12.5px;font-weight:700;
  color:var(--c-red,#dc2626);background:var(--c-redLight,#fef2f2);
  border:1px solid color-mix(in srgb, var(--c-red,#dc2626) 25%, transparent);}
.mgate-dots{display:flex;direction:ltr;justify-content:center;gap:13px;margin:18px 0;height:15px;align-items:center;}
.mgate-dot{width:13px;height:13px;border-radius:50%;border:2px solid var(--c-mutedLight,#94a3b8);
  background:transparent;box-sizing:border-box;transition:all .15s ease;}
.mgate-dot.fill{background:var(--c-green,#198754);border-color:var(--c-green,#198754);transform:scale(1.12);}
.mgate-dots.err{animation:mgateShake .3s ease;}
.mgate-keypad{display:grid;direction:ltr;grid-template-columns:repeat(3,1fr);gap:10px;width:100%;}
.mgate-key{min-height:58px;border-radius:14px;cursor:pointer;touch-action:manipulation;
  background:var(--c-surface,#ffffff);color:var(--c-ink,#212529);border:1px solid var(--c-border,#e2e8f0);
  font-size:21px;font-weight:700;font-family:inherit;
  box-shadow:0 1px 2px rgba(2,8,23,.07);
  display:flex;align-items:center;justify-content:center;
  transition:transform .1s ease, background .12s ease;}
.mgate-key:active{transform:scale(.93);background:var(--c-faint,#f1f5f9);}
.mgate-key:disabled{opacity:.55;cursor:default;transform:none;}
.mgate-key.fn{color:var(--c-muted,#64748b);}
`;

export default function ManagerGate({
    open = false,
    action,
    context = null,
    salesDate = null,
    onApprove = () => {},
    onCancel = () => {},
}) {
    const { tenantId, employeeId } = useAuth();
    const { t } = useLanguage();

    const [pin, setPin] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState('');
    const [shake, setShake] = useState(false);

    useEffect(() => {
        if (!open) return;
        setPin('');
        setVerifying(false);
        setError('');
        setShake(false);
    }, [open]);

    const fail = useCallback((message) => {
        setError(message);
        setPin('');
        setShake(true);
        setTimeout(() => setShake(false), 350);
    }, []);

    const submitPin = useCallback(async (pinValue) => {
        setVerifying(true);
        setError('');
        try {
            const approval = await managerApprovalService.verify({
                tenantId,
                pin: pinValue,
                action,
                context,
                requestedByEmployeeId: employeeId || null,
                salesDate,
            });
            setPin('');
            onApprove(approval);
        } catch (err) {
            // Only a 401 actually means the PIN was wrong.
            //
            // This used to report EVERYTHING except a 403 as "Invalid
            // credentials", which sends a manager to retype a PIN that was
            // correct all along. Seen for real on 2026-08-27: the POS build
            // targets production, /manager-approvals/verify has never been
            // promoted past staging, so prod answers 404 — and the gate said
            // the credentials were invalid. No amount of retyping could fix
            // that, and nothing on screen hinted why.
            //
            //   401 → wrong PIN (the only case worth retrying)
            //   403 → real credential, but not a manager
            //   else → the check could not be performed at all (endpoint
            //          missing, server down, offline). Say so, because the
            //          fix is not "try again".
            const status = err?.response?.status;
            if (status === 403) fail(t('manager_access_required'));
            else if (status === 401) fail(t('invalid_credentials'));
            else fail(t('manager_approval_unavailable', {}, 'Approval unavailable. Check your connection or contact support.'));
        } finally {
            setVerifying(false);
        }
    }, [tenantId, action, context, employeeId, salesDate, onApprove, fail, t]);

    const pressDigit = useCallback((digit) => {
        if (verifying || pin.length >= PIN_LENGTH) return;
        setError('');
        const next = pin + digit;
        setPin(next);
        if (next.length === PIN_LENGTH) submitPin(next);
    }, [verifying, pin, submitPin]);

    const pressBackspace = useCallback(() => {
        if (verifying) return;
        setError('');
        setPin((prev) => prev.slice(0, -1));
    }, [verifying]);

    const onPress = useCallback((fn) => (e) => {
        if (e && typeof e.button === 'number' && e.button !== 0) return;
        fn();
    }, []);

    // Hardware-keyboard entry. The pad is touch-first, but a till often has a
    // USB keyboard attached (and the simulator only has one), so typing the PIN
    // has to work. This delegates to pressDigit/pressBackspace/onCancel rather
    // than reimplementing them, so the verifying guard, the 6-digit cap and the
    // auto-submit on the sixth digit are identical on both paths by
    // construction — there is no second copy of that logic to drift.
    //
    // Listener is on window because nothing inside the portaled dialog holds
    // focus (the keys are buttons the user taps, never tabs to), so a
    // container-level handler would receive nothing.
    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (e) => {
            // Let browser/OS shortcuts through untouched (⌘Q, ctrl+W, alt+tab…).
            if (e.ctrlKey || e.altKey || e.metaKey) return;

            if (/^[0-9]$/.test(e.key)) {          // main row and numpad both emit '0'–'9'
                e.preventDefault();
                pressDigit(e.key);
                return;
            }
            if (e.key === 'Backspace') {
                e.preventDefault();                // legacy "Backspace navigates back"
                pressBackspace();
                return;
            }
            if (e.key === 'Escape') {
                // Mirrors the on-screen ✕, which is disabled while verifying:
                // closing mid-flight would let an in-flight approval resolve
                // and fire onApprove on a gate the user had already dismissed.
                if (verifying) return;
                e.preventDefault();
                onCancel();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, verifying, pressDigit, pressBackspace, onCancel]);

    if (!open) return null;

    return createPortal(
        <div
            className="mgate-scrim"
            data-testid="manager-gate"
            role="dialog"
            aria-modal="true"
            aria-label={t('manager_approval_required')}
        >
            <style>{CSS}</style>
            <div className="mgate-panel">
                <h3 className="mgate-title">{t('manager_approval_required')}</h3>
                <p className="mgate-sub">{t(`manager_approval_for_${action}`)}</p>

                {error ? <p className="mgate-err" role="alert">{error}</p> : null}

                <div className={`mgate-dots${shake ? ' err' : ''}`} aria-hidden="true">
                    {Array.from({ length: PIN_LENGTH }, (_, i) => (
                        <span key={i} className={`mgate-dot${i < pin.length ? ' fill' : ''}`} />
                    ))}
                </div>

                <div className="mgate-keypad">
                    {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                        <button
                            key={d}
                            type="button"
                            className="mgate-key"
                            disabled={verifying}
                            onPointerDown={onPress(() => pressDigit(d))}
                        >
                            {d}
                        </button>
                    ))}
                    <button
                        type="button"
                        className="mgate-key fn"
                        disabled={verifying}
                        onPointerDown={onPress(pressBackspace)}
                        aria-label={t('delete')}
                    >
                        &larr;
                    </button>
                    <button
                        type="button"
                        className="mgate-key"
                        disabled={verifying}
                        onPointerDown={onPress(() => pressDigit('0'))}
                    >
                        0
                    </button>
                    <button
                        type="button"
                        className="mgate-key fn"
                        disabled={verifying}
                        onPointerDown={onPress(onCancel)}
                        aria-label={t('cancel')}
                    >
                        &#10005;
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
